// =============================================================================
// DriverRespond - the driver's answer to a care team ride
// (20270110100000_ride_driver_accept).
//
// pending (the ride waits for the driver): "Accept" or "Can't make it".
// Can't make it offers three ways out, so the patient still gets a ride:
//   Ask another driver        a CHW or care manager; they must accept in turn
//   I'll book a Medicaid ride the ride becomes a Medicaid request and its
//                             Ride help task goes to this driver
//   Send to the front desk    a Medicaid request for the Front Desk
// All through ride_driver_respond (only the ride's driver).
// Used in the ride box (RideDriverPanel) and on the Ride alerts card.
//
// home (20270111100000_ride_home_safety): the ride home, after pickup. The
// driver taking the patient home accepts it ("home_accept"); "Can't do the
// ride home" asks another driver ("home_to") or turns it into a Medicaid
// will-call ride home for the front desk ("home_nemt"). The driver, the ride
// home driver, or the office can hand it on.
// =============================================================================
import { useEffect, useState } from "react";
import { C } from "../../lib/tokens";
import { Btn } from "../ui";
import { rideDriverRespond, fetchDrivers } from "../../lib/ridesApi";
import { DRIVER_HANDOFFS, HOME_HANDOFFS, respondDoneText, rideErrorText } from "../../lib/ridesCore";

const inp = { minHeight: 34, padding: "6px 8px", fontSize: 13, fontFamily: "inherit", border: "1px solid " + C.borderMid, borderRadius: 6, background: C.bgPrimary, color: C.textPrimary };

export default function DriverRespond({ rideId, driverId = null, pending = false, home = false, startOpen = false, onBack = null, onDone }) {
  const handoffs = home ? HOME_HANDOFFS : DRIVER_HANDOFFS;
  const toAction = home ? "home_to" : "hand_to";
  const [open, setOpen] = useState(startOpen);
  const [how, setHow] = useState(null);
  const [drivers, setDrivers] = useState(null);
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let live = true;
    if (how === toAction && drivers === null) {
      fetchDrivers().then((d) => { if (live) setDrivers((d || []).filter((u) => u.id !== driverId)); })
        // supabase-best-effort: without the list the driver can still send it to the front desk.
        .catch(() => { if (live) setDrivers([]); });
    }
    return () => { live = false; };
  }, [how, drivers, driverId, toAction]);

  const send = async (action) => {
    if (action === toAction && !to) { setErr("Pick who should drive."); return; }
    setBusy(true); setErr(null);
    try {
      await rideDriverRespond(rideId, action, { driverId: action === toAction ? to : null, reason });
      const name = action === toAction ? ((drivers || []).find((u) => u.id === to) || {}).full_name : "";
      setOpen(false); setHow(null);
      if (onDone) onDone(action, respondDoneText(action, name));
    } catch (e) { setErr(rideErrorText(e)); }
    setBusy(false);
  };

  if (!open) {
    return (
      <div data-testid="driver-respond" style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {pending && <span style={{ fontSize: 12, fontWeight: 600, color: C.warnFg, flex: "1 1 100%" }}>{home ? "Can you take the patient home?" : "Can you drive this ride?"}</span>}
        {pending && <Btn size="sm" variant="success" disabled={busy} onClick={() => send(home ? "home_accept" : "accept")}>Accept</Btn>}
        <Btn size="sm" variant="outline" disabled={busy} onClick={() => setOpen(true)}>{home ? "Can't do the ride home" : "Can't make it"}</Btn>
        {err && <div role="alert" style={{ fontSize: 12, color: C.dangerSolid, flexBasis: "100%" }}>{err}</div>}
      </div>
    );
  }

  return (
    <div data-testid="driver-respond-handoff" style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
      <div role="radiogroup" aria-label="Who takes the ride" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {handoffs.map((h) => (
          <Btn key={h.value} size="sm" variant={how === h.value ? "primary" : "outline"} disabled={busy}
            onClick={() => { setHow(h.value); setErr(null); }}>{h.label}</Btn>
        ))}
      </div>
      {how === toAction && (
        <select aria-label="Driver to ask" value={to} onChange={(e) => setTo(e.target.value)} disabled={!drivers} style={inp}>
          <option value="">{drivers ? "Pick a CHW or care manager..." : "Loading..."}</option>
          {(drivers || []).map((u) => <option key={u.id} value={u.id}>{u.full_name + " (" + u.role + ")"}</option>)}
        </select>
      )}
      {how && how !== toAction && (
        <input aria-label="Why you can't drive" placeholder="Why (optional)" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} style={inp} />
      )}
      {how && (
        <div style={{ fontSize: 11, color: C.textTertiary }}>
          {how === toAction && "They get an alert and have to accept it."}
          {how === "home_nemt" && "The front desk gets a task to book a will-call Medicaid ride home. Add your miles for the trip there."}
          {how === "nemt_self" && "The ride becomes a Medicaid ride request. You get the Ride help task to book it with the plan."}
          {how === "front_desk" && "The front desk gets a High task to book a Medicaid ride."}
        </div>
      )}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {how && <Btn size="sm" variant="primary" disabled={busy} onClick={() => send(how)}>{busy ? "Sending..." : "Send"}</Btn>}
        <Btn size="sm" variant="outline" disabled={busy} onClick={() => { setOpen(false); setHow(null); setErr(null); if (onBack) onBack(); }}>Back</Btn>
      </div>
      {err && <div role="alert" style={{ fontSize: 12, color: C.dangerSolid }}>{err}</div>}
    </div>
  );
}
